import { Request, Response, NextFunction } from 'express';
import { verifyJWT } from '../utils/helpers/utilities.services';
import User from '../Modules/Auth/models/auth.model';
import Role from '../Modules/Role/models/role.model';
import { HttpException } from '../utils/exceptions/httpException';
import { Types } from 'mongoose';
import { status } from '../utils/helpers/api.responses';
import { logger } from '../utils/logger';
import { RolesEnum, StatusEnum } from '../Modules/Auth/interfaces/auth.interface';

export const getAuthenticatedUser = async (req: Request) => {
    const token = req.headers.authorization?.split(' ')[1];
    const ip = req.ip || req.headers['x-forwarded-for'] || req.connection.remoteAddress;

    if (!token) {
        throw new HttpException(
            status.Unauthorized,
            req.t('General.empty', { field: req.t('User.token') })
        );
    }

    const decoded = verifyJWT(token, req.t);
    if (!decoded) {
        throw new HttpException(
            status.Unauthorized,
            req.t('General.invalid', { field: req.t('User.token') })
        );
    }

    const user = await User.findOne({ _id: decoded._id });
    if (!user || !user.sessionId || user.sessionId !== decoded.sessionId) {
        throw new HttpException(status.Unauthorized, req.t('General.sessionExpired'));
    }

    if (user.status !== StatusEnum.ACTIVE) {
        throw new HttpException(
            status.Unauthorized,
            req.t('General.invalid', { field: req.t('User.user') })
        );
    }

    const currentTime = Date.now();
    if (user.tokenExpiry && currentTime > user.tokenExpiry.getTime()) {
        throw new HttpException(
            status.Unauthorized,
            req.t('General.expired', { field: req.t('User.token') })
        );
    }

    logger.info(`Authenticated user: ${user._id}, IP: ${ip}, URL: ${req.originalUrl}`);
    return user;
};

export const authMiddleware = async (req: Request, res: Response, next: NextFunction) => {
    try {
        const user = await getAuthenticatedUser(req);
        req.user = user;
        next();
    } catch (error) {
        if (error instanceof HttpException) {
            return res.status(error.status).json({
                status: error.status,
                message: error.message,
            });
        }
        return res.status(status.InternalServerError).json({
            status: status.InternalServerError,
            message: req.t('General.error'),
        });
    }
};

export const isUserPermissionArray = async (req: Request, res: Response, next: NextFunction) => {
    try {
        const user = await getAuthenticatedUser(req);
        const userRole = user.roleId instanceof Types.ObjectId
            ? await Role.findById(user.roleId.toString())
            : user.roleId;

        if (!userRole) {
            throw new HttpException(
                status.Unauthorized,
                req.t('Role.invalid_role_id', { field: req.t('Role.role') })
            );
        }

        if (userRole.name.toLocaleLowerCase() === RolesEnum.ADMIN.toLocaleLowerCase()) {
            req.user = user;
            return next();
        }

        let roleIds: string[] = [];

        if (Array.isArray(req.body) && req.body.every(item => item.roleId)) {
            roleIds = req.body.map(item => item.roleId);
        } else if (Array.isArray(req.body.roleId)) {
            const usersToDelete = await User.find({ _id: { $in: req.body.roleId } }, { roleId: 1 });
            roleIds = usersToDelete.map(user => user.roleId.toString());
        } else {
            throw new HttpException(status.BadRequest, req.t('General.invalid', { field: 'request structure' }));
        }

        // Validate role IDs
        const roles = await Role.find({ _id: { $in: roleIds } });
        if (roles.length !== roleIds.length) {
            throw new HttpException(
                status.BadRequest,
                req.t('Role.invalid_role_id', { field: req.t('Role.role') })
            );
        }

        if (userRole.name.toLocaleLowerCase() === RolesEnum.ADMIN.toLocaleLowerCase()) {
            req.user = user;
            return next();
        }

        throw new HttpException(status.Forbidden, req.t('Role.permission_denied'));
    } catch (error) {
        if (error instanceof HttpException) {
            return res.status(error.status).json({
                status: error.status,
                message: error.message,
            });
        }
        return res.status(status.InternalServerError).json({
            status: status.InternalServerError,
            message: req.t('General.error'),
        });
    }
};

export const isuserCreatePermission = async (req: Request, res: Response, next: NextFunction) => {
    try {
        const user = await getAuthenticatedUser(req);
        const userRole = user.roleId instanceof Types.ObjectId
            ? await Role.findById(user.roleId.toString())
            : user.roleId;

        if (!userRole) {
            throw new HttpException(
                status.Unauthorized,
                req.t('Role.invalid_role_id', { field: req.t('Role.role') })
            );
        }

        if (userRole.name.toLocaleLowerCase() === RolesEnum.ADMIN.toLocaleLowerCase()) {
            req.user = user;
            return next();
        }

        const targetRoleId = req.body.roleId;
        if (!targetRoleId) {
            throw new HttpException(
                status.BadRequest,
                req.t('General.required', { field: req.t('Role.role') })
            );
        }

        const targetedRole = await Role.findById(targetRoleId.toString());
        if (!targetedRole) {
            throw new HttpException(
                status.BadRequest,
                req.t('Role.invalid_role_id', { field: req.t('Role.role') })
            );
        }

        if (userRole.name.toLocaleLowerCase() === RolesEnum.ADMIN.toLocaleLowerCase()) {
            req.user = user;
            return next();
        }


        throw new HttpException(status.Forbidden, req.t('Role.permission_denied'));
    } catch (error) {
        if (error instanceof HttpException) {
            return res.status(error.status).json({
                status: error.status,
                message: error.message,
            });
        }
        return res.status(status.InternalServerError).json({
            status: status.InternalServerError,
            message: req.t('General.error'),
        });
    }
};


export const isAdmin = async (req: Request, res: Response, next: NextFunction) => {
    try {
        const user = await getAuthenticatedUser(req);
        const userRole = user.roleId instanceof Types.ObjectId
            ? await Role.findById(user.roleId.toString())
            : user.roleId;

        if (!userRole) {
            throw new HttpException(
                status.InternalServerError,
                req.t('General.error')
            );
        }
        if (
            userRole.name.toLowerCase() !== RolesEnum.ADMIN.toLowerCase()
        ) {
            throw new HttpException(status.Forbidden, req.t('Role.permission_denied'));
        }

        req.user = user;
        next();
    } catch (error) {
        if (error instanceof HttpException) {
            return res.status(error.status).json({
                status: error.status,
                message: error.message,
            });
        }
        return res.status(status.InternalServerError).json({
            status: status.InternalServerError,
            message: req.t('General.error'),
        });
    }
};

export const restrictToSelfOrAdminCreator = async (req: Request, res: Response, next: NextFunction) => {
    try {
        const user = await User.findById(req.user._id).populate<{
            roleId: { name: string };
        }>('roleId').lean();

        if (!user) {
            throw new HttpException(status.Unauthorized, req.t('General.invalid', { field: req.t('User.user') }));
        }
        const isSelf = req.user._id.toString() === req.params.id.toString();

        if (isSelf) {
            req.user = user;
            return next();
        }


        const targetUser = await User.findOne({
            _id: req.params.id,
            isDeleted: false
        }).populate<{ roleId: { name: string } }>('roleId').lean();

        if (!targetUser) {
            throw new HttpException(
                status.NotFound,
                req.t('General.not_found', { field: req.t('User.user') })
            );
        }
        const targetRole = targetUser.roleId.name.toLowerCase();
        const userRole = user.roleId.name.toLowerCase();

        if (userRole === RolesEnum.ADMIN.toLowerCase()) {
            req.user = user;
            return next();
        }

        if (
            userRole === RolesEnum.STAFF.toLowerCase() &&
            targetRole === RolesEnum.LABOUR.toLowerCase()
        ) {
            req.user = user;
            return next();
        }


        throw new HttpException(status.Forbidden, req.t('Role.permission_denied'));
    } catch (error) {
        if (error instanceof HttpException) {
            return res.status(error.status).json({
                status: error.status,
                message: error.message,
            });
        }
        return res.status(status.InternalServerError).json({
            status: status.InternalServerError,
            message: req.t('General.error'),
        });
    }
};

export const checkPermissions = (requiredPermissions: string | string[]) => {
    return async (req: Request, res: Response, next: NextFunction) => {
        try {
            const user = await getAuthenticatedUser(req);
            const permissionsToCheck = Array.isArray(requiredPermissions)
                ? requiredPermissions
                : [requiredPermissions];
            const userId = user._id.toString();
            const roleId = user.roleId;

            const role = await Role.findById(roleId).populate({
                path: 'permissions',
                match: { isActive: true },
                select: 'name',
            });

            if (!role) {
                throw new HttpException(
                    status.InternalServerError,
                    req.t('General.error')
                );
            }

            if (role.name.toLocaleLowerCase() === RolesEnum.ADMIN.toLocaleLowerCase()) {
                req.user = user;
                return next();
            }

            const rolePermissions = role.permissions.map((perm: any) => perm.name);

            const userData = await User.findById(userId)
                .populate({
                    path: 'permissions.permissionId',
                    match: { isActive: true },
                    select: 'name',
                })
                .populate({
                    path: 'restrictedPermissions.permissionId',
                    match: { isActive: true },
                    select: 'name',
                });

            const userPermissions = userData.permissions.map((perm: any) => perm.permissionId.name);
            const restrictedPermissions = userData.restrictedPermissions.map((perm: any) => perm.permissionId.name);
            const finalPermissions = [...new Set([...rolePermissions, ...userPermissions])].filter(
                perm => !restrictedPermissions.includes(perm)
            ).map(prem => prem.toLocaleLowerCase());

            const required = permissionsToCheck.map(p => p.toLowerCase())
            if (!required.some(permission => finalPermissions.includes(permission))) {
                throw new HttpException(status.Forbidden, req.t('Role.permission_denied'));
            }

            req.user = user;
            next();
        } catch (error) {
            if (error instanceof HttpException) {
                return res.status(error.status).json({
                    status: error.status,
                    message: error.message,
                });
            }
            return res.status(status.InternalServerError).json({
                status: status.InternalServerError,
                message: req.t('General.error'),
            });
        }
    };
};

